// Vistas de casos (objeto vistaCaso, D34): lista, conteo y casos de cada vista, contados y filtrados en el servidor.
import { buscarEnCasos, enOrden } from './busqueda';
import { type EstadoCaso } from './casos';
import { gqlFilas, gqlTodos } from './gql';
import { ApiError, request } from './http';
import { create, listAll, remove, update } from './rest';
import { filtroDeVista, type Asignado, type Ctx as CtxVista, type VistaCaso } from '../lib/vistasCaso';
import { cursoresDe, ordenMotor, type PaginaCasos, planRecorrido } from '../lib/ordenCasos';

type RawVista = {
  id: string;
  name: string;
  orden: number | null;
  estados: string[] | null;
  grupos: string[] | null;
  gruposExcluidos: string[] | null;
  etiquetas: string[] | null;
  asignado: string | null;
  tituloContiene: string | null;
  miembros: string[] | null;
  activa: boolean | null;
};

const aVista = (r: RawVista): VistaCaso => ({
  id: r.id,
  nombre: r.name,
  orden: r.orden ?? 99,
  estados: (r.estados ?? []) as EstadoCaso[],
  grupos: r.grupos ?? [],
  gruposExcluidos: r.gruposExcluidos ?? [],
  etiquetas: r.etiquetas ?? [],
  asignado: (['yo', 'nadie'].includes(r.asignado ?? '') ? r.asignado : '') as Asignado,
  tituloContiene: r.tituloContiene ?? '',
  miembros: r.miembros ?? [],
  activa: r.activa !== false,
});

export const vistasCaso = async () => (await listAll<RawVista>('vistasCaso', { orderBy: 'orden[AscNullsLast]' }, 200)).map(aVista).sort((a, b) => a.orden - b.orden);

export type VistaInput = Omit<VistaCaso, 'id'>;
const cuerpo = (v: VistaInput) => ({
  name: v.nombre.trim(),
  orden: v.orden,
  estados: v.estados,
  grupos: v.grupos,
  gruposExcluidos: v.gruposExcluidos,
  etiquetas: v.etiquetas,
  asignado: v.asignado,
  tituloContiene: v.tituloContiene.trim(),
  miembros: v.miembros,
  activa: v.activa,
});
export const crearVista = (v: VistaInput) => create('vistasCaso', cuerpo(v));
export const editarVista = (id: string, v: VistaInput) => update('vistasCaso', id, cuerpo(v));
export const borrarVista = (id: string) => remove('vistasCaso', id);

// Al renombrar un grupo, las vistas que lo nombran pasan al nombre nuevo (si no, quedarian ocultas). Devuelve cuantas.
export async function renombrarGrupoEnVistas(antes: string, despues: string) {
  const cambia = (xs: string[]) => xs.map((g) => (g === antes ? despues : g));
  const afectadas = (await vistasCaso()).filter((v) => v.grupos.includes(antes) || v.gruposExcluidos.includes(antes));
  for (const v of afectadas) await editarVista(v.id, { ...v, grupos: cambia(v.grupos), gruposExcluidos: cambia(v.gruposExcluidos) });
  return afectadas.length;
}

type Ctx = { miembroId?: string; grupo?: string; q?: string; gruposPermitidos?: string[] };

// Cuantos casos cumplen un filtro del motor (contado en el servidor, sin limite de pagina).
export async function contarCasos(f: Record<string, unknown>) {
  const r = await request<{ data?: { casos?: { totalCount: number } }; errors?: { message: string }[] }>('/graphql', {
    method: 'POST',
    body: { query: 'query($f:CasoFilterInput){ casos(filter:$f){ totalCount } }', variables: { f } },
  });
  if (r.errors?.length) throw new ApiError(400, r.errors[0].message);
  return r.data?.casos?.totalCount ?? 0;
}

// Cuantos casos tiene cada vista (una consulta por vista: el motor no deja repetir "casos" en la misma).
export async function contarVistas(vistas: VistaCaso[], ctx: Ctx) {
  return new Map(await Promise.all(vistas.map(async (v) => [v.id, await contarCasos(filtroDeVista(v, ctx))] as const)));
}

// Campos de las listas de casos (vistas, "sin cerrar", Mi dia, contadores): sin los textos largos que solo usa el
// caso abierto (siguiente paso y respuesta sugerida de la IA, notas internas). Con 1000 casos cada 2 min era lo que
// mas pesaba. El resumen de la IA si va: se ve en las tarjetas y en la busqueda.
export const CAMPOS_VISTA =
  'id name numero estado prioridad grupo asignadoId duenoZammad hiloId hilosUnidos correoCliente origen etiquetas ultimoMensaje primeraRespuesta cerradoEn ultimoCliente ultimoEquipo seguimientoEn resumenIA zammadId personaId empresaId createdAt persona{ id name{ firstName lastName } } empresa{ id name }';
// Lo que le falta a un caso de la lista para abrirlo (Casos.tsx lo pide aparte y lo junta).
export const CAMPOS_DETALLE = 'id siguientePasoIA respuestaIA notasInternas';
// Un caso suelto completo (abierto por enlace, desde un correo, en "Abiertos ahora").
export const CAMPOS_LISTA = `${CAMPOS_VISTA} siguientePasoIA respuestaIA notasInternas`;

// Con busqueda, los ids que encontro el buscador (D45). Si no responde, queda la busqueda simple de filtroDeVista.
async function conBusqueda(ctx: CtxVista): Promise<CtxVista> {
  if (!ctx.q?.trim()) return ctx;
  const b = await buscarEnCasos(ctx.q);
  return b ? { ...ctx, ids: b.ids } : ctx;
}

// Casos de una vista (los mas recientes primero; con busqueda, los mas relevantes). La lista llega hasta `TOPE_LISTA`;
// el total sale de contarVista.
export const TOPE_LISTA = 300;
export async function casosDeVista<T extends { id: string }>(v: VistaCaso, ctx: CtxVista, max = TOPE_LISTA): Promise<T[]> {
  const c = await conBusqueda(ctx);
  if (!c.ids) return gqlTodos<T>('casos', CAMPOS_VISTA, { filtro: filtroDeVista(v, c), orden: [{ ultimoMensaje: 'DescNullsLast' }], max });
  // Por tandas en orden de relevancia, para que los filtros de la vista no dejen fuera lo mas relevante.
  const out: T[] = [];
  for (let i = 0; i < c.ids.length && out.length < max; i += TOPE_LISTA) {
    const tanda = c.ids.slice(i, i + TOPE_LISTA);
    out.push(...(await gqlTodos<T>('casos', CAMPOS_VISTA, { filtro: filtroDeVista(v, { ...c, ids: tanda }), max: max - out.length })));
  }
  return enOrden(out, c.ids).slice(0, max);
}

// Cuantos casos de la vista cumplen tambien la busqueda.
export const contarVista = async (v: VistaCaso, ctx: CtxVista) => contarCasos(filtroDeVista(v, await conBusqueda(ctx)));

/* ---------------- una pagina de la lista, con orden del servidor (Bandeja -> Casos) ---------------- */

// Ids que cumplen la vista y la busqueda, en orden de relevancia del buscador (solo ids: liviano). Se guardan un momento
// para no repetir la consulta en cada pagina.
const idsRelevantes = new Map<string, { hasta: number; p: Promise<string[]> }>();
function idsDeBusqueda(v: VistaCaso, c: CtxVista & { ids: string[] }) {
  const filtro = filtroDeVista(v, c);
  const clave = JSON.stringify(filtro);
  const x = idsRelevantes.get(clave);
  if (x && x.hasta > Date.now()) return x.p;
  const p = gqlTodos<{ id: string }>('casos', 'id', { filtro, max: c.ids.length }).then((xs) => enOrden(xs, c.ids).map((y) => y.id));
  p.catch(() => idsRelevantes.delete(clave));
  idsRelevantes.set(clave, { hasta: Date.now() + 15_000, p });
  if (idsRelevantes.size > 20) idsRelevantes.delete(idsRelevantes.keys().next().value as string);
  return p;
}

// Una pagina de casos de la vista con el orden elegido, hecho por el motor (aplica a todos los que cumplen, no solo a
// los cargados). El motor no tiene "offset": la pagina se pide con el cursor de la fila anterior (o el de la siguiente,
// hacia atras); si no se conoce, se llega con un recorrido liviano (solo cursores) desde el borde conocido mas cercano o
// desde el final. Con busqueda y sin orden elegido, en orden de relevancia. `total`: los casos de la vista (contarVista),
// para saber cual es la ultima pagina; null si aun no se sabe.
export async function paginaDeVista<T extends { id: string }>(v: VistaCaso, ctx: CtxVista, p: Omit<PaginaCasos, 'explicito'> & { explicito?: boolean }, total: number | null): Promise<T[]> {
  const c = await conBusqueda(ctx);
  const antes = (p.pag - 1) * p.tam;
  if (c.ids && !p.explicito) {
    const tramo = (await idsDeBusqueda(v, { ...c, ids: c.ids })).slice(antes, antes + p.tam);
    return tramo.length ? enOrden(await gqlTodos<T>('casos', CAMPOS_VISTA, { filtro: { id: { in: tramo } }, max: tramo.length }), tramo) : [];
  }
  const filtro = filtroDeVista(v, c);
  const orden = ordenMotor(p);
  const cache = cursoresDe(JSON.stringify([filtro, orden, p.tam]));
  if (total !== null && antes >= total && p.pag > 1) return [];
  const n = total !== null ? Math.min(p.tam, total - antes) : p.tam;
  const pedir = (o: { despues?: string | null; antes?: string | null; n: number }) => gqlFilas<T>('casos', CAMPOS_VISTA, { filtro, orden, ...o });
  let filas: { cursor: string; node?: T }[];
  const previa = antes ? cache.fila(antes) : null;
  const siguiente = cache.fila(antes + n + 1);
  if (!antes || previa) filas = await pedir({ despues: previa, n });
  else if (siguiente) filas = await pedir({ antes: siguiente, n });
  else if (total !== null && antes + n >= total) filas = (await gqlFilas<T>('casos', CAMPOS_VISTA, { filtro, orden: ordenMotor(p, true), n })).reverse();
  else {
    // Recorrido liviano hasta la ultima fila de la pagina anterior; se guardan los bordes de pagina que cruza.
    const r = planRecorrido(antes, total, cache.conocidas());
    let cursores: string[];
    let primera: number;
    if (r.tipo === 'adelante') {
      cursores = (await gqlFilas('casos', '', { filtro, orden, despues: r.cursor, n: r.n })).map((x) => x.cursor);
      primera = r.desde + 1;
    } else if (r.tipo === 'atras') {
      cursores = (await gqlFilas('casos', '', { filtro, orden, antes: r.cursor, n: r.n })).map((x) => x.cursor);
      primera = r.desde - r.n;
    } else {
      cursores = (await gqlFilas('casos', '', { filtro, orden: ordenMotor(p, true), n: r.n })).map((x) => x.cursor).reverse();
      primera = antes;
    }
    cursores.forEach((cur, i) => {
      const pos = primera + i;
      if (pos % p.tam === 0 || (pos - 1) % p.tam === 0) cache.guardarFila(pos, cur);
    });
    const ancla = cache.fila(antes);
    if (!ancla) return [];
    filas = await pedir({ despues: ancla, n });
  }
  if (filas.length) cache.guardar(p.pag, { inicio: filas[0].cursor, fin: filas.at(-1)!.cursor }, p.tam, filas.length);
  return filas.map((x) => x.node as T);
}
